// =============================================================================
// BOUTON D'EXPORT DU PLANNING (PDF)
// =============================================================================

import React, { useState, useMemo } from 'react';
import { Download, FileText, Loader2, HardHat } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { pdf } from '@react-pdf/renderer';
import { format, getWeek } from 'date-fns';
import { fr } from 'date-fns/locale';
import { PlanningPDF } from '@/components/pdf/PlanningPDF';
import { PlanningTerrainPDF } from '@/components/pdf/PlanningTerrainPDF';
import { usePlanningStore } from '@/stores/planningStore';
import type { CalendarAffectation } from '@/types/calendar';
import type { ViewMode } from '@/components/calendar/hooks/useCalendar';

interface Chantier {
  id: string;
  nom_chantier: string;
}

interface Equipe {
  id: string;
  nom: string;
}

interface PlanningExportButtonProps {
  viewMode: ViewMode;
  currentDate: Date;
  affectations: CalendarAffectation[];
  entrepriseNom?: string;
  chantiers?: Chantier[];
  equipes?: Equipe[];
}

export function PlanningExportButton({
  viewMode,
  currentDate,
  affectations,
  entrepriseNom,
  chantiers = [],
  equipes = [],
}: PlanningExportButtonProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState<'semaine' | 'mois' | 'terrain' | null>(null);

  // Récupérer les filtres depuis le store
  const filters = usePlanningStore((state) => state.filters);

  // Construire les filtres actifs pour le PDF
  const activeFilters = useMemo(() => {
    const chantierNames = filters.chantierIds
      .map(id => chantiers.find(c => c.id === id)?.nom_chantier)
      .filter(Boolean) as string[];

    const equipeNames = filters.equipeIds
      .map(id => equipes.find(e => e.id === id)?.nom)
      .filter(Boolean) as string[];

    return {
      chantierNames: chantierNames.length > 0 ? chantierNames : undefined,
      equipeNames: equipeNames.length > 0 ? equipeNames : undefined,
      searchQuery: filters.searchQuery || undefined,
    };
  }, [filters, chantiers, equipes]);

  // Libellé court du filtre actif pour affichage dans le menu
  const filterLabel = useMemo(() => {
    // Chantiers filtrés
    if (activeFilters.chantierNames && activeFilters.chantierNames.length > 0) {
      if (activeFilters.chantierNames.length === 1) {
        return activeFilters.chantierNames[0];
      }
      if (activeFilters.chantierNames.length === 2) {
        return `${activeFilters.chantierNames[0]}, ${activeFilters.chantierNames[1]}`;
      }
      // 3+ chantiers : "Nom1, Nom2 +X"
      return `${activeFilters.chantierNames[0]}, ${activeFilters.chantierNames[1]} +${activeFilters.chantierNames.length - 2}`;
    }
    // Équipes filtrées
    if (activeFilters.equipeNames && activeFilters.equipeNames.length > 0) {
      if (activeFilters.equipeNames.length === 1) {
        return activeFilters.equipeNames[0];
      }
      if (activeFilters.equipeNames.length === 2) {
        return `${activeFilters.equipeNames[0]}, ${activeFilters.equipeNames[1]}`;
      }
      // 3+ équipes : "Nom1, Nom2 +X"
      return `${activeFilters.equipeNames[0]}, ${activeFilters.equipeNames[1]} +${activeFilters.equipeNames.length - 2}`;
    }
    // Recherche texte active
    if (activeFilters.searchQuery) {
      return activeFilters.searchQuery;
    }
    return '';
  }, [activeFilters]);

  // Générer et télécharger le PDF
  const handleExport = async (mode: 'semaine' | 'mois') => {
    setLoading(mode);

    try {
      const doc = (
        <PlanningPDF
          viewMode={mode}
          currentDate={currentDate}
          affectations={affectations}
          entrepriseNom={entrepriseNom}
          activeFilters={activeFilters}
        />
      );

      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);

      // Nom du fichier selon le mode
      const fileName = mode === 'semaine'
        ? `planning-semaine-${getWeek(currentDate, { weekStartsOn: 1 })}-${format(currentDate, 'yyyy')}.pdf`
        : `planning-${format(currentDate, 'MMMM-yyyy', { locale: fr })}.pdf`;

      // Télécharger
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: 'PDF genere',
        description: `Le planning ${mode === 'semaine' ? 'hebdomadaire' : 'mensuel'} a ete telecharge.`,
      });
    } catch (error) {
      console.error('[PlanningExport] Erreur:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de generer le PDF.',
        variant: 'destructive',
      });
    } finally {
      setLoading(null);
    }
  };

  // Ouvrir l'aperçu pour impression
  const handlePrint = async (mode: 'semaine' | 'mois') => {
    setLoading(mode);

    try {
      const doc = (
        <PlanningPDF
          viewMode={mode}
          currentDate={currentDate}
          affectations={affectations}
          entrepriseNom={entrepriseNom}
          activeFilters={activeFilters}
        />
      );

      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);

      // Ouvrir dans un nouvel onglet pour impression
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
      }

      toast({
        title: 'Apercu ouvert',
        description: 'Le planning est pret pour impression.',
      });
    } catch (error) {
      console.error('[PlanningExport] Erreur impression:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de preparer l\'impression.',
        variant: 'destructive',
      });
    } finally {
      setLoading(null);
    }
  };

  // Export PDF terrain (format simplifié pour équipes sur le terrain)
  const handleExportTerrain = async () => {
    setLoading('terrain');

    try {
      const doc = (
        <PlanningTerrainPDF
          currentDate={currentDate}
          affectations={affectations}
          entrepriseNom={entrepriseNom}
          activeFilters={activeFilters}
        />
      );

      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);

      // Nom du fichier
      const fileName = `planning-terrain-semaine-${getWeek(currentDate, { weekStartsOn: 1 })}-${format(currentDate, 'yyyy')}.pdf`;

      // Télécharger
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: 'PDF terrain genere',
        description: 'Le planning terrain a ete telecharge.',
      });
    } catch (error) {
      console.error('[PlanningExport] Erreur terrain:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de generer le PDF terrain.',
        variant: 'destructive',
      });
    } finally {
      setLoading(null);
    }
  };

  const isLoading = loading !== null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={isLoading}
          className="gap-2"
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
          Exporter
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 z-[70]">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Telecharger PDF
        </DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() => handleExport('semaine')}
          disabled={loading === 'semaine'}
        >
          <FileText className="mr-2 h-4 w-4 text-blue-600" />
          Planning Semaine
          {loading === 'semaine' && <Loader2 className="ml-auto h-4 w-4 animate-spin" />}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleExport('mois')}
          disabled={loading === 'mois'}
        >
          <FileText className="mr-2 h-4 w-4 text-green-600" />
          Planning Mois
          {loading === 'mois' && <Loader2 className="ml-auto h-4 w-4 animate-spin" />}
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Planning Terrain
        </DropdownMenuLabel>
        <DropdownMenuItem
          onClick={handleExportTerrain}
          disabled={loading === 'terrain'}
        >
          <HardHat className="mr-2 h-4 w-4 text-orange-600" />
          Export Terrain (Semaine)
          {loading === 'terrain' && <Loader2 className="ml-auto h-4 w-4 animate-spin" />}
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Imprimer
        </DropdownMenuLabel>
        <DropdownMenuItem onClick={() => handlePrint(viewMode)}>
          <FileText className="mr-2 h-4 w-4" />
          <span>
            Imprimer vue actuelle ({viewMode === 'semaine' ? 'Semaine' : 'Mois'})
            {filterLabel && <span className="text-sky-400 font-medium"> {filterLabel}</span>}
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default PlanningExportButton;
